import React from 'react'
import api from 'configs/api'
import { alertSuccess, alertDanger } from 'utils/toast'
import { translate } from 'locales'
import axios from 'axios'

const mapEmergencyContactFromApi = (emergencyContact: any) => {
  if (!emergencyContact) return emergencyContact

  return {
    name: emergencyContact.contact_name ?? emergencyContact.name ?? '',
    phone: emergencyContact.contact_phone ?? emergencyContact.phone ?? '',
    relationship:
      emergencyContact.contact_relationship ??
      emergencyContact.relationship ??
      '',
  }
}

export const findUserData = async (
  setUserData: React.Dispatch<React.SetStateAction<any>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
  try {
    const find = await api.get('/doctors/ms/v1/me')

    const dataStorage: any = window.localStorage.getItem('@StarBem:user')
    const dataParse = JSON.parse(dataStorage)

    const specialties: { id: string; name: string; slug?: string }[] = []
    find.data.specialties.forEach((item: any) => {
      const temp = {
        id: item.id,
        name: item.name,
        slug: item.slug,
        type: 1,
        status: item.status,
      }

      if (item.status === 'active') {
        specialties.push(temp)
      }
    })

    const data = {
      ...dataParse,
      ...find.data,
      profile: { ...dataParse?.profile, photo: find.data.photo },
      specialties,
      emergency_contact: mapEmergencyContactFromApi(
        find.data.emergency_contact
      ),
    }

    setUserData({
      ...dataParse,
      ...data,
    })

    window.localStorage.setItem(
      '@StarBem:user',
      JSON.stringify({ ...dataParse, ...data })
    )

    setLoading(false)
  } catch (err: any) {
    alertDanger(
      err?.response?.data?.message || translate('errors.errorLoadingData')
    )
    setLoading(false)
  }
}

export const handleAreaSelection = (
  e: any,
  area: any,
  userData: any,
  setUserData: React.Dispatch<React.SetStateAction<any>>
) => {
  const updatedOccupations = userData.specialties.filter(
    (item: any) => item.id !== area.id
  )

  if (e.target.checked) {
    updatedOccupations.push({
      id: area.id,
      name: area.name,
      type: 1,
    })
  } else {
    updatedOccupations.push({ id: area.id, name: area.name, type: 0 })
  }

  setUserData({ ...userData, specialties: updatedOccupations })
}

export const updateUserData = async (
  data: any,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
  setLoading(true)

  const body = {
    name: data.name,
    email: data.email,
    gender: data.gender,
    birthdate: data.birthdate,
    cellphone: data.cellphone,
    document: data.document,
    bio: data.bio,
    professional_document_type: data.professional_document_type,
    professional_document_number: data.professional_document_number,
    professional_document_uf: data.professional_document_uf,
    cnpj: data.cnpj,
    rg: data.rg,
    professional_phone: data.professional_phone,
    shirt_size: data.shirt_size,
    rqe: data.rqe,
    residential_address: data.residential_address,
    professional_address: data.professional_address,
    emergency_contact: data.emergency_contact,
  }

  try {
    await api.put(`/doctors/ms/v1/user/${data.id}`, body)

    alertSuccess(translate('success.dataUpdate'))
    setLoading(false)
  } catch (err: any) {
    setLoading(false)
    alertDanger(
      err?.response?.data?.message || translate('errors.internalError')
    )
  }
}

export const updatePassword = async (
  password: string,
  userId: string,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setNewPassword: React.Dispatch<React.SetStateAction<string>>,
  setNewPasswordConfirm: React.Dispatch<React.SetStateAction<string>>
) => {
  setLoading(true)

  try {
    await api.put(`/doctors/v1/users/${userId}`, { password })

    alertSuccess(translate('success.dataUpdate'))
    setNewPassword('')
    setNewPasswordConfirm('')
  } catch (err: any) {
    alertDanger(
      err?.response?.data?.message || translate('errors.internalError')
    )
  } finally {
    setLoading(false)
  }
}

export const updateProfileBio = async (
  bio: string,
  userId: string,
  isAIGenerated: boolean = false
): Promise<void> => {
  try {
    const locales = window.localStorage.getItem('@StarBem:lang')
    const lang = locales === 'pt' || !locales ? 'pt-BR' : 'en-US'

    await api.put(`doctors/ms/v1/user/${userId}/bios`, {
      lang,
      bio,
      bio_ia_generated: isAIGenerated,
    })
  } catch (err: any) {
    throw new Error(
      err?.response?.data?.message || translate('errors.internalError')
    )
  }
}

export const uploadProfilePhoto = async (
  file: File,
  doctorId: string,
  photo_ia_generated: boolean
): Promise<void> => {
  const data = new FormData()
  data.append('file', file)
  data.append('photo_ia_generated', String(photo_ia_generated))

  await api.post(`/doctors/ms/v1/user/${doctorId}/upload`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export const generateBioWithAI = async (
  professionalInfo: string
): Promise<string> => {
  const token: any = window.localStorage.getItem('token')

  if (!token) {
    throw new Error('Token do médico não encontrado. Faça login novamente.')
  }

  const response = await axios.post(
    'https://flow.starbem.app/webhook/create/bio',
    { text: professionalInfo },
    {
      headers: {
        Authorization: 'Bearer ' + token,
      },
    }
  )

  if (response.data?.bio || response.data?.text) {
    return response.data.bio || response.data.text
  }

  throw new Error('Não foi possível gerar a bio. Tente novamente.')
}

export const getProfileBio = async (userId: string): Promise<string | null> => {
  try {
    const response = await api.get(`doctors/ms/v1/user/${userId}/bios`)
    const bios = response.data?.bios || []

    const locales = window.localStorage.getItem('@StarBem:lang')
    const lang = locales === 'pt' || !locales ? 'pt-BR' : 'en-US'

    const bioInLang = bios.find((bioItem: any) => bioItem.lang === lang)

    return bioInLang?.bio || null
  } catch (err: any) {
    if (err?.response?.status === 404) {
      return null
    }
    throw new Error(
      err?.response?.data?.message || translate('errors.internalError')
    )
  }
}

export const generatePictureWithAI = async (image: File): Promise<File> => {
  const token: any = window.localStorage.getItem('token')

  if (!token) {
    throw new Error('Token do médico não encontrado. Faça login novamente.')
  }

  const formData = new FormData()
  formData.append('image', image, image.name)

  const response = await axios.post<Blob>(
    'https://flow.starbem.app/webhook/melhorar-foto',
    formData,
    {
      responseType: 'blob',
      headers: {
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const blob = response.data

  if (!blob || blob.size === 0) {
    throw new Error('Resposta vazia ao gerar a imagem.')
  }

  if (blob.type?.includes('json')) {
    const text = await blob.text()
    let message = 'Não foi possível gerar a imagem.'
    try {
      const parsed = JSON.parse(text) as { message?: string; error?: string }
      message = parsed.message || parsed.error || message
    } catch {
      // mantém mensagem padrão
    }
    throw new Error(message)
  }

  const mimeFromBlob =
    blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg'

  const extension =
    mimeFromBlob === 'image/png'
      ? 'png'
      : mimeFromBlob === 'image/gif'
        ? 'gif'
        : mimeFromBlob === 'image/webp'
          ? 'webp'
          : 'jpg'

  const stem =
    image.name
      .replace(/\.[^.]+$/, '')
      .replace(/[/\\]/g, '')
      .trim() || 'photo'
  const fileName = `${stem}.${extension}`

  return new File([blob], fileName, {
    type: mimeFromBlob,
    lastModified: Date.now(),
  })
}
